import { Download } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import FMR1ActionsAPI from "../../../../api/fmr1";
import { useAppDispatch, useAppSelector } from "../../../../app/store";
import ButtonV2 from "../../../../components/ui/button/v2";
import { ErrorDisplay } from "../../../../components/ui/error/ErrorDisplay";
import { CustomLoadingSpinner } from "../../../../components/ui/spinners/LoadingSpinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../../../components/ui/tabs/Tabs";
import { showErrorToast, showSuccessToast } from "../../../../lib/toast";
import { cn } from "../../../../lib/utils";
import {
  discardFMR1UiChanges,
  selectFMR1IsDirtyOverall,
  selectFMR1UpsertRequestFromDraft,
} from "../../../reporting/logic/frm1Slice";
import { fetchFMR1Thunk, upsertFMR1Thunk } from "../../../reporting/logic/frm1Thunk";
import styles from "../ro/components/ROPage.module.css";

import { SaveButtonV2 } from "../../../../components/ui/button/v2/SaveButton";
import { ShimmerBlock } from "../common/ReportDetailsShimmering";
import FMR1GeneralInformationForm from "./generalInformation/FMR1GeneralInformationForm";
import FMR1Part1Tab from "./Part1/FMR1Part1Tab";
import FMR1Part2Tab from "./Part2/FMR1Part2Tab";
import FMR1SignatoryInfoForm from "./signatoryInfo/FMR1SignatoryInfoForm";
import { RETRIEVING_DATA_SUBTITLE } from "../../../../lib/constants";

type TabItem = {
  label: string;
  value: string;
  content: React.ReactNode;
};

const FMR1Page: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const fetchStatus = useAppSelector((state) => (state as any).fmr1Data.fetch_status);
  const upsertStatus = useAppSelector((state) => (state as any).fmr1Data.upsert_status);
  const isDirtyOverall = useAppSelector(selectFMR1IsDirtyOverall);
  const upsertRequest = useAppSelector(selectFMR1UpsertRequestFromDraft);
  const hasServerData = fetchStatus === "succeeded";

  const [isDownloading, setIsDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("general-info");
  const isSaving = upsertStatus === "pending";
  const isPageLoading = fetchStatus === "pending";
  const pageError = fetchStatus === "failed" ? "Failed to load FMR1 report" : null;

  const loadFMR1 = useCallback(async () => {
    if (!case_id) return;
    await dispatch(fetchFMR1Thunk(case_id));
  }, [case_id, dispatch]);

  useEffect(() => {
    loadFMR1();
  }, [loadFMR1]);

  const handleSaveFMRDetails = useCallback(async () => {
    try {
      if (!case_id) return;

      const res = await dispatch(
        upsertFMR1Thunk({
          case_id,
          request: upsertRequest,
        }),
      );

      if (upsertFMR1Thunk.fulfilled.match(res)) {
        showSuccessToast("Changes saved.");
      } else if (upsertFMR1Thunk.rejected.match(res) && !res.meta.aborted) {
        const msg = (res.payload as string) || res.error?.message || "Failed to save FMR1";
        showErrorToast(msg);
      }
    } finally {
      /* saving status comes from redux */
    }
  }, [case_id, dispatch, upsertRequest]);

  const handleDiscard = useCallback(() => {
    if (!case_id) return;
    dispatch(discardFMR1UiChanges());
  }, [case_id, dispatch]);

  const renderDiscardButton = () => (
    <ButtonV2
      variant="outline"
      onClick={handleDiscard}
      disabled={!isDirtyOverall || isSaving}
      className={cn(
        "font-sans font-medium text-sm leading-5 w-[75.5px] h-8 border-[#3551F3] text-[#3551F3] py-3",
        (!isDirtyOverall || isSaving) && "opacity-50 cursor-not-allowed",
      )}
    >
      Discard
    </ButtonV2>
  );

  const renderSaveButton = () => (
    <SaveButtonV2
      disabled={!isDirtyOverall || isSaving}
      onClick={handleSaveFMRDetails}
      buttonText={isSaving ? "Saving..." : "Save"}
    />
  );

  const renderHeader = () => (
    <div className={cn(styles.topHeader, "flex items-center justify-end")}>
      <div className="flex gap-3">
        {isPageLoading ? (
          <div className="flex gap-3 animate-pulse">
            <ShimmerBlock className="h-8 w-[75.5px]" />
            <ShimmerBlock className="h-8 w-[75.5px]" />
          </div>
        ) : (
          <>
            {renderDiscardButton()}
            {renderSaveButton()}
          </>
        )}
      </div>
    </div>
  );

  const handleTabValueChange = useCallback((nextValue: string) => {
    setActiveTab(nextValue);
  }, []);

  const tabItems: TabItem[] = useMemo(
    () => [
      {
        label: "General Information",
        value: "general-info",
        content: <FMR1GeneralInformationForm />,
      },
      {
        label: "FMR-1(1)",
        value: "fmr-1-1-1",
        content: <FMR1Part1Tab />,
      },
      {
        label: "FMR-1(2)",
        value: "fmr-1-1-2",
        content: <FMR1Part2Tab />,
      },
      {
        label: "Signatory Info",
        value: "signatory-info",
        content: <FMR1SignatoryInfoForm />,
      },
    ],
    [],
  );

  const renderTabTriggers = () =>
    tabItems.map((item) => (
      <TabsTrigger
        key={item.value}
        value={item.value}
        className="text-sm font-normal data-[state=active]:bg-transparent data-[state=active]:shadow-0 px-4 py-4"
      >
        {item.label}
      </TabsTrigger>
    ));

  const renderTabContents = () =>
    tabItems.map((item) => (
      <TabsContent key={item.value} value={item.value} className="mt-0 overflow-visible">
        {hasServerData ? item.content : null}
      </TabsContent>
    ));

  const handleDownload = useCallback(async () => {
    if (!case_id) {
      return;
    }

    try {
      setIsDownloading(true);
      const blob = await FMR1ActionsAPI.download(case_id);
      showSuccessToast("Download Completed");

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `FMR1_${case_id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      showErrorToast("Error Download");
    } finally {
      setIsDownloading(false);
    }
  }, [case_id]);

  const renderTabContainer = () => (
    <Tabs value={activeTab} onValueChange={handleTabValueChange} className="w-[1032px]">
      <div className="w-[1032px] mx-auto flex justify-between items-center bg-gray-50 border-b">
        <TabsList className="flex-1 min-w-0 w-full flex justify-start bg-gray-50 p-0 rounded-none">
          {renderTabTriggers()}
        </TabsList>
        <div className="flex justify-end gap-3 mr-2.5 shrink-0">
          <ButtonV2
            variant="outline-primary"
            size="md"
            onClick={handleDownload}
            loading={isDownloading}
            disabled={isDownloading}
            prefix={!isDownloading ? <Download className="h-4 w-4" /> : undefined}
          >
            {isDownloading ? "Downloading..." : "Download"}
          </ButtonV2>
        </div>
      </div>
      <div className="w-[1032px] mx-auto bg-gray-50 overflow-visible">{renderTabContents()}</div>
    </Tabs>
  );

  const renderFMR1ReportsBody = () => (
    <div className={cn(styles.pageContainer, "flex alitems-center justify-center")}>
      <div className="bg-gray-50 rounded-t-xl">
        <div className="w-[1032px] mx-auto h-[52px] px-2.5 py-1.5 flex items-center justify-between">
          <span className="truncate text-[#0A0A0A] font-sans text-lg font-medium leading-7">
            Fraud Monitoring Return 1
          </span>
        </div>
        {renderTabContainer()}
      </div>
    </div>
  );

  const renderLoadingState = () => (
    <div className="flex flex-1 flex-col min-h-0 min-w-0 w-full box-border p-6">
      <CustomLoadingSpinner
        className="h-full w-full min-h-0 flex-1 m-0"
        title="Loading FMR1..."
        subtitle={RETRIEVING_DATA_SUBTITLE}
      />
    </div>
  );

  return (
    <div className="flex flex-col min-h-[100dvh] min-w-0 w-full bg-[#F3F4F6]">
      {renderHeader()}
      <div className="flex flex-1 flex-col min-h-0 min-w-0 w-full">
        {isPageLoading ? (
          renderLoadingState()
        ) : pageError ? (
          <ErrorDisplay
            error={pageError}
            onRetry={loadFMR1}
            isRetrying={isPageLoading}
          />
        ) : hasServerData ? (
          renderFMR1ReportsBody()
        ) : (
          renderLoadingState()
        )}
      </div>
    </div>
  );
};

export default FMR1Page;
